import type { ReactNode } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import { RouteFallback } from '../components/RouteFallback';
import { useAuth } from '../contexts/AuthContext';
import { buildLoginRedirectPath, getSafePostLoginDestination } from '../lib/authRedirect';

/**
 * Protected Route component
 * Intent: Ensure users are authenticated before accessing protected pages
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!user) {
    return (
      <Navigate
        to={buildLoginRedirectPath(`${location.pathname}${location.search}${location.hash}`)}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Public Route component
 * Intent: Redirect authenticated users away from login/signup pages
 */
export function PublicRoute({
  static: isStatic,
  children,
}: {
  static?: boolean;
  children: ReactNode;
}) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isStatic && user) {
    return <Navigate to={getSafePostLoginDestination(location.search)} replace />;
  }

  return <>{children}</>;
}
